// ============================================================
//  src/pages/integrations/IntegrationsPage.js
//
//  User-level AI provider settings — connect OpenAI, Claude,
//  Gemini. Keys are stored encrypted on the server; only a
//  masked version is shown after saving.
//  Styles: ./IntegrationsPage.css (scoped under .aii-*)
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { listAiIntegrations, saveAiIntegration, deleteAiIntegration } from '../../api/client';
import './IntegrationsPage.css';

// ── Provider catalogue ────────────────────────────────────────
//  Model `value`s are what the backend expects — change with care.
const PROVIDERS = [
  {
    id:       'openai',
    name:     'OpenAI',
    mark:     'O',
    color:    '#10a37f',
    tagline:  'GPT-4o, GPT-4o mini, GPT-3.5 Turbo',
    keyHint:  'sk-…',
    keyNote:  'Starts with sk-',
    docsUrl:  'https://platform.openai.com/api-keys',
    models: [
      { value: 'gpt-4o-mini',   label: 'GPT-4o mini', note: 'Fast, cheap' },
      { value: 'gpt-4o',        label: 'GPT-4o', note: 'Smart, balanced' },
      { value: 'gpt-4-turbo',   label: 'GPT-4 Turbo', note: 'Most capable' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', note: 'Legacy' },
    ],
  },
  {
    id:       'claude',
    name:     'Anthropic Claude',
    mark:     'C',
    color:    '#d97706',
    tagline:  'Claude Haiku, Sonnet, Opus',
    keyHint:  'sk-ant-…',
    keyNote:  'Starts with sk-ant-',
    docsUrl:  'https://console.anthropic.com/settings/keys',
    models: [
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku', note: 'Fastest' },
      { value: 'claude-sonnet-4-6',         label: 'Claude Sonnet', note: 'Balanced' },
      { value: 'claude-opus-4-6',           label: 'Claude Opus', note: 'Most capable' },
    ],
  },
  {
    id:       'gemini',
    name:     'Google Gemini',
    mark:     'G',
    color:    '#4285f4',
    tagline:  'Gemini 1.5 Flash, Pro, Ultra',
    keyHint:  'AIza…',
    keyNote:  'Google AI Studio API key',
    docsUrl:  'https://aistudio.google.com/app/apikey',
    models: [
      { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', note: 'Fast, cheap' },
      { value: 'gemini-1.5-pro',   label: 'Gemini 1.5 Pro', note: 'Balanced' },
      { value: 'gemini-1.0-pro',   label: 'Gemini 1.0 Pro', note: 'Legacy' },
    ],
  },
];

const modelLabel = (provider, value) => provider.models.find(m => m.value === value)?.label || value;

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T'));
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ProviderMark({ provider, size = 'md' }) {
  return (
    <span className={`aii-mark ${size}`} style={{ '--c': provider.color }} aria-hidden="true">
      {provider.mark}
    </span>
  );
}

// ── Connect / update sheet ────────────────────────────────────
function ConnectSheet({ provider, saved, onClose, onSave }) {
  const [apiKey, setApiKey]   = useState('');
  const [model, setModel]     = useState(saved?.model || provider.models[0].value);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving]   = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose, saving]);

  async function submit(e) {
    e.preventDefault();
    if (!apiKey.trim()) {
      toast.error('Please enter your API key');
      inputRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      await onSave(provider.id, apiKey.trim(), model);
      toast.success(`${provider.name} ${saved ? 'updated' : 'connected'}`);
      onClose();
    } catch {
      /* error toast already shown by parent */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="aii-sheet-backdrop" onClick={() => !saving && onClose()}>
      <form className="aii-sheet" onClick={e => e.stopPropagation()} onSubmit={submit} role="dialog" aria-label={`Connect ${provider.name}`}>
        <span className="aii-sheet-grip" />
        <div className="aii-sheet-head">
          <ProviderMark provider={provider} size="lg" />
          <div>
            <h3>{saved ? `Update ${provider.name}` : `Connect ${provider.name}`}</h3>
            <p>{saved ? 'Paste a new key to replace the saved one.' : 'Your key is encrypted before it’s stored.'}</p>
          </div>
          <button type="button" className="aii-icon-btn" onClick={onClose} disabled={saving} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>

        <div className="aii-field">
          <div className="aii-label-row">
            <label htmlFor="aii-key">API key</label>
            <a href={provider.docsUrl} target="_blank" rel="noreferrer">Get a key <i className="fa-solid fa-arrow-up-right-from-square" /></a>
          </div>
          <div className="aii-key-wrap">
            <i className="fa-solid fa-key aii-key-ic" />
            <input
              id="aii-key"
              ref={inputRef}
              type={showKey ? 'text' : 'password'}
              placeholder={provider.keyHint}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="none"
            />
            <button type="button" className="aii-icon-btn" onClick={() => setShowKey(s => !s)} aria-label={showKey ? 'Hide key' : 'Show key'}>
              <i className={`fa-solid ${showKey ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          </div>
          <span className="aii-hint">{provider.keyNote}</span>
        </div>

        <div className="aii-field">
          <div className="aii-label-row">
            <label>Default model</label>
            <span className="aii-hint">You can override it per command</span>
          </div>
          <div className="aii-models" role="radiogroup">
            {provider.models.map(m => (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={model === m.value}
                className={`aii-model ${model === m.value ? 'is-active' : ''}`}
                onClick={() => setModel(m.value)}
              >
                <span className="aii-radio" />
                <span className="aii-model-txt"><b>{m.label}</b><em>{m.note}</em></span>
              </button>
            ))}
          </div>
        </div>

        <div className="aii-sheet-actions">
          <button type="button" className="aii-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="aii-btn primary" disabled={saving}>
            {saving ? <><span className="aii-spin" /> Saving…</> : <>{saved ? 'Update key' : 'Save & connect'} <i className="fa-solid fa-arrow-right" /></>}
          </button>
        </div>
        <p className="aii-secure"><i className="fa-solid fa-lock" /> Keys are encrypted at rest and only ever shown masked.</p>
      </form>
    </div>
  );
}

// ── Provider card ─────────────────────────────────────────────
function ProviderCard({ provider, saved, onOpen, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [showKey, setShowKey]       = useState(false);
  const isConnected = !!saved;

  async function remove() {
    setDeleting(true);
    try {
      const ok = await onDelete(provider.id);
      if (ok) toast.success(`${provider.name} disconnected`);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <article className={`aii-card ${isConnected ? 'is-connected' : ''}`} style={{ '--c': provider.color }}>
      <div className="aii-card-head">
        <ProviderMark provider={provider} />
        <div className="aii-card-id">
          <h3>{provider.name}</h3>
          <p>{provider.tagline}</p>
        </div>
        <span className={`aii-status ${isConnected ? 'is-on' : ''}`}>
          <span className="aii-status-dot" /> {isConnected ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {isConnected ? (
        <dl className="aii-details">
          <div>
            <dt>API key</dt>
            <dd>
              <code>{showKey ? saved.keyMasked : '••••••••••••'}</code>
              <button type="button" className="aii-text-btn" onClick={() => setShowKey(s => !s)}>{showKey ? 'Hide' : 'Show'}</button>
            </dd>
          </div>
          <div><dt>Default model</dt><dd>{saved.model ? modelLabel(provider, saved.model) : 'Account default'}</dd></div>
          <div><dt>Last updated</dt><dd>{fmtDate(saved.updatedAt)}</dd></div>
        </dl>
      ) : (
        <ul className="aii-perks">
          <li><i className="fa-solid fa-check" /> Use {provider.name.split(' ').pop()} in any bot command</li>
          <li><i className="fa-solid fa-check" /> {provider.models.length} models to choose from</li>
          <li><i className="fa-solid fa-check" /> Billed directly by the provider</li>
        </ul>
      )}

      {confirming ? (
        <div className="aii-confirm" role="alert">
          <p><i className="fa-solid fa-triangle-exclamation" /> Bots using {provider.name} will stop replying with AI. Remove it?</p>
          <div className="aii-confirm-actions">
            <button type="button" className="aii-btn ghost sm" onClick={() => setConfirming(false)} disabled={deleting}>Cancel</button>
            <button type="button" className="aii-btn danger sm" onClick={remove} disabled={deleting}>
              {deleting ? <><span className="aii-spin light" /> Removing…</> : 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        <div className="aii-card-actions">
          {isConnected ? (
            <>
              <button type="button" className="aii-btn ghost" onClick={() => onOpen(provider.id)}>
                <i className="fa-solid fa-key" /> Update key
              </button>
              <button type="button" className="aii-btn danger-ghost icon" onClick={() => setConfirming(true)} aria-label={`Remove ${provider.name}`} title="Remove">
                <i className="fa-regular fa-trash-can" />
              </button>
            </>
          ) : (
            <button type="button" className="aii-btn dark full" onClick={() => onOpen(provider.id)}>
              <i className="fa-solid fa-plug" /> Connect {provider.name.split(' ').pop()}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// ── Usage docs ────────────────────────────────────────────────
const OPTIONS = [
  ['provider',       'openai · claude · gemini'],
  ['model',          'Override the account default model for this command'],
  ['system_prompt',  'Personality and instructions for the AI'],
  ['memory',         'true = remember the conversation per user session'],
  ['memory_trigger', 'Shared memory key across multiple commands (optional)'],
  ['max_tokens',     'Max reply length (default: 500)'],
  ['temperature',    '0.0 = precise · 1.0 = creative (default: 0.7)'],
  ['save_as',        <>Store the reply in <code>{'{{ai.reply}}'}</code> for later actions</>],
];

function UsageDoc() {
  const [copied, setCopied] = useState(false);
  const snippet = JSON.stringify([
    {
      type:          'ai_reply',
      provider:      'openai',
      model:         'gpt-4o-mini',
      system_prompt: 'You are a helpful WhatsApp assistant. Keep replies short.',
      memory:        true,
      max_tokens:    400,
    },
  ], null, 2);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Failed to copy'));
  };

  return (
    <section className="aii-usage">
      <div className="aii-section-head">
        <h2>Use AI in your commands</h2>
        <p>Add an <code>ai_reply</code> action to any command — your saved key is used automatically.</p>
      </div>

      <div className="aii-usage-grid">
        <div className="aii-code">
          <div className="aii-code-bar">
            <span className="aii-code-dots"><i /><i /><i /></span>
            <span>command actions · JSON</span>
            <button type="button" onClick={copy} className={copied ? 'is-copied' : ''}>
              <i className={`fa-${copied ? 'solid fa-check' : 'regular fa-copy'}`} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre>{snippet}</pre>
        </div>

        <div className="aii-options">
          <div className="aii-options-head">Options</div>
          {OPTIONS.map(([k, v]) => (
            <div key={k} className="aii-opt">
              <code>{k}</code>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [openId,       setOpenId]       = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listAiIntegrations();
      if (res.data?.success) {
        setIntegrations(res.data.integrations ?? []);
      } else {
        setError(res.data?.error || 'Failed to load integrations');
      }
    } catch (err) {
      console.error('Load integrations error:', err);
      setError('Network error. Please check your connection.');
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (provider, apiKey, model) => {
    try {
      const res = await saveAiIntegration(provider, apiKey, model);
      if (res.data?.success) {
        await load();
        return true;
      }
      const errorMsg = res.data?.error ?? 'Failed to save integration';
      throw new Error(errorMsg);
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err?.response?.data?.error || err.message || 'Failed to save integration');
      throw err;
    }
  };

  const handleDelete = async (provider) => {
    try {
      const res = await deleteAiIntegration(provider);
      if (res.data?.success) {
        await load();
        return true;
      }
      toast.error(res.data?.error ?? 'Failed to remove integration');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to remove integration');
    }
    return false;
  };

  const savedMap = Object.fromEntries(integrations.map(i => [i.provider, i]));
  const connectedCount = PROVIDERS.filter(p => savedMap[p.id]).length;
  const openProvider = PROVIDERS.find(p => p.id === openId);
  const closeSheet = useCallback(() => setOpenId(null), []);

  return (
    <div className="aii">
      <header className="aii-head">
        <div>
          <span className="aii-eyebrow"><i className="fa-solid fa-wand-magic-sparkles" /> AI Integrations</span>
          <h1>Give your bots a brain</h1>
          <p>Connect your AI provider keys once and use them across every bot. Your own key is used, so usage is billed by the provider directly.</p>
        </div>
        {!loading && !error && (
          <div className="aii-summary">
            <div className="aii-summary-ring" style={{ '--p': connectedCount / PROVIDERS.length }}>
              <span>{connectedCount}/{PROVIDERS.length}</span>
            </div>
            <div>
              <b>{connectedCount ? `${connectedCount} provider${connectedCount === 1 ? '' : 's'} connected` : 'No providers yet'}</b>
              <span><i className="fa-solid fa-lock" /> Encrypted at rest</span>
            </div>
          </div>
        )}
      </header>

      {loading ? (
        <div className="aii-grid">
          {[0, 1, 2].map(i => (
            <div key={i} className="aii-card aii-skel"><span className="sk av" /><span className="sk w60" /><span className="sk w80" /><span className="sk w40" /><span className="sk btn" /></div>
          ))}
        </div>
      ) : error ? (
        <div className="aii-error">
          <span className="aii-error-ic"><i className="fa-solid fa-plug-circle-xmark" /></span>
          <h3>Couldn’t load your integrations</h3>
          <p>{error}</p>
          <button type="button" className="aii-btn dark" onClick={() => { setLoading(true); load(); }}>
            <i className="fa-solid fa-rotate-right" /> Try again
          </button>
        </div>
      ) : (
        <>
          <div className="aii-grid">
            {PROVIDERS.map(p => (
              <ProviderCard
                key={p.id}
                provider={p}
                saved={savedMap[p.id] ?? null}
                onOpen={setOpenId}
                onDelete={handleDelete}
              />
            ))}
          </div>
          <UsageDoc />
        </>
      )}

      {openProvider && (
        <ConnectSheet
          provider={openProvider}
          saved={savedMap[openProvider.id] ?? null}
          onClose={closeSheet}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
